import type { components } from "@/api-schema";
import { PathsV1GroupManualMembershipSearchPostParametersQueryExtend, UserStatus } from "@/api-schema-enums";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useGroupManualMembershipSearchQuery } from "@/common/api/group-manual-membership/use-group-manual-membership-search.query";
import { useGroupTemporaryUserQuery } from "@/common/api/group/use-group-temporary-user.query";
import { useGroupUserQuery } from "@/common/api/group/use-group-user.query";
import { useMemo } from "react";

type User = components["schemas"]["User"];
type MembershipSource = "rule" | "temporary" | "manual";

export type ExtendedUser = components["schemas"]["User"] & {
  rule?: boolean;
  temporary?: boolean;
  manual?: boolean;
  duplicate?: boolean;
};

export enum UserFilter {
  All = "all",
  Rule = "rule",
  Temporary = "temporary",
  Manual = "manual",
  Duplicate = "duplicate",
}

export function useGroupUsers(groupUuid: string, userSearchTerm: string, userFilter: UserFilter) {
  const groupUsersQueryInstance = useGroupUserQuery(groupUuid);
  const groupTemporaryUsersQueryInstance = useGroupTemporaryUserQuery(groupUuid);
  const groupManualMembershipsQueryInstance = useGroupManualMembershipSearchQuery({
    options: {
      parameters: {
        extend: [PathsV1GroupManualMembershipSearchPostParametersQueryExtend.User],
        pageSize: UNLIMITED_PAGE_SIZE,
      },
      body: { group: [groupUuid] },
    },
  });

  const groupUsersUnion: ExtendedUser[] = useMemo(() => {
    const groupUsers: User[] = groupUsersQueryInstance.data || [];
    const groupTemporaryUsers: User[] = groupTemporaryUsersQueryInstance.data || [];
    const groupManualMembershipUsers: User[] =
      groupManualMembershipsQueryInstance.data?.data
        ?.map((membership) => membership.user)
        .filter((user): user is User => user !== undefined) || [];

    const usersByUuid = new Map<string, ExtendedUser>();

    const mergeUser = (user: User, source: MembershipSource) => {
      const existingUser = usersByUuid.get(user.uuid);

      usersByUuid.set(user.uuid, {
        ...existingUser,
        ...user,
        [source]: true,
      });
    };

    groupUsers.forEach((user) => mergeUser(user, "rule"));
    groupTemporaryUsers.forEach((user) => mergeUser(user, "temporary"));
    groupManualMembershipUsers.forEach((user) => mergeUser(user, "manual"));

    const combinedUsers = Array.from(usersByUuid.values()).map((user) => {
      const membershipCount = [user.rule, user.temporary, user.manual].filter(Boolean).length;

      return {
        ...user,
        duplicate: membershipCount >= 2 ? true : undefined,
      };
    });

    // Sort by lastName first, then by firstName
    const result = combinedUsers.sort((a, b) => {
      const lastNameComparison = (a.lastName || "").localeCompare(b.lastName || "");
      if (lastNameComparison !== 0) {
        return lastNameComparison;
      }
      return (a.firstName || "").localeCompare(b.firstName || "");
    });

    return result;
  }, [groupUsersQueryInstance.data, groupTemporaryUsersQueryInstance.data, groupManualMembershipsQueryInstance.data]);

  const enabledUsers = useMemo(() => groupUsersUnion.filter((u) => u.status === UserStatus.Enabled), [groupUsersUnion]);

  const userStats = useMemo(() => {
    const ruleUsersCount = enabledUsers.filter((u) => u.rule).length;
    const temporaryUsersCount = enabledUsers.filter((u) => u.temporary).length;
    const duplicateUsersCount = enabledUsers.filter((u) => u.duplicate).length;
    const manualUsersCount = enabledUsers.filter((u) => u.manual).length;

    return {
      total: enabledUsers.length,
      rule: ruleUsersCount,
      temporary: temporaryUsersCount,
      duplicate: duplicateUsersCount,
      manual: manualUsersCount,
    };
  }, [enabledUsers]);

  const filteredUsers = useMemo(() => {
    return enabledUsers
      .filter((u) => {
        if (userFilter === UserFilter.Rule) return u.rule;
        if (userFilter === UserFilter.Temporary) return u.temporary;
        if (userFilter === UserFilter.Manual) return u.manual;
        if (userFilter === UserFilter.Duplicate) return u.duplicate;
        return true;
      })
      .filter((u) => {
        if (!userSearchTerm) return true;
        const searchTerm = userSearchTerm.toLowerCase();
        return (
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm) ||
          u.email?.toLowerCase().includes(searchTerm)
        );
      });
  }, [enabledUsers, userFilter, userSearchTerm]);

  return {
    filteredUsers,
    userStats,
    isLoading:
      groupUsersQueryInstance.isLoading ||
      groupTemporaryUsersQueryInstance.isLoading ||
      groupManualMembershipsQueryInstance.isLoading,
  };
}
